import { subscriptionContract } from '@daymade/api-contracts';
import { ClientInferResponseBody } from '@ts-rest/core';
import { EntryMethod } from 'components/enter/EntryMethod';
import { sha256 } from 'js-sha256';
import mixpanel, { Callback, Dict } from 'mixpanel-browser';
import { TOrderDetails } from 'types/OrderDetails';
import {
  formattedPriceStringFromMicroCurrency,
  formattedPriceStringFromPence,
  sleep,
} from 'utils/common';
import {
  APP_ENDPOINT,
  ASSET_PREFIX,
  runningInProduction,
} from 'utils/constants';
import { getCookie } from 'utils/cookie';

/** Set this to true once DRIP has launch. Once DRIP has been live for a few weeks, it can be deleted and assumed to be true */
export const isDripLaunched = true;


// REMEMBER to set overrideAnalyticsEnabled to false before merging to main!
export const overrideAnalyticsEnabled = false;

export const analyticsEnabled = () => {
  if (runningInProduction && overrideAnalyticsEnabled) {
    // If this error is thrown, then 'overrideAnalyticsEnabled' was left set to true whilst a build from main was
    // attempted. Change 'overrideAnalyticsEnabled' back to false, commit the change, push, and then rebuild.
    throw new Error(
      'Both overrideAnalyticsEnabled and runningInProduction were true. See comments near this message'
    );
  }

  if (!isDripLaunched) {
    // Disable analytics if DRIP is not launched, since most tracking tags do not have a staging mode
    return false;
  }

  return overrideAnalyticsEnabled || runningInProduction;
};


mixpanel.init(
  (runningInProduction && isDripLaunched)
    ? 'e3331ff766fd35af0ae326639e3a695f'
    : '2c7038670f7590faa18853d724f044b3',
  {
    debug: !runningInProduction,
    api_host: 'https://api-eu.mixpanel.com',
    track_pageview: true,
    cookie_domain: '.daymade.co.uk',
    record_sessions_percent: 0,
  }
);

export const optOutTracking = () => {
  if (!mixpanel.has_opted_out_tracking()) {
    mixpanel.track('Opt Out Tracking');
    mixpanel.opt_out_tracking();
  }
};

export const optInTracking = () => {
  if (!mixpanel.has_opted_in_tracking()) {
    mixpanel.opt_in_tracking();
  }
};

export const track_links = (id: string, event: string) => {
  mixpanel.track_links(id, event);
};

// Facebook pixel page views are tracked automatically, so this just fire a GA event for page views.
export const trackPageView = (url: string) => {
  if (!analyticsEnabled()) return;

  // GTM
  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: 'pageview',
      page: url,
    });
  }

  // Snapchat
  if (window.snaptr) {
    window.snaptr('track', 'PAGE_VIEW');
  }

  // TikTok
  // NOTE that we do not send page views to TikTok - There is a ViewContent event, but this is more for viewing products

  // Sendinblue
  if (window.sendinblue) {
    window.sendinblue.page(url);
  }

  mixpanel.track_pageview({ page: url });
};

export const trackEvent = (eventName: string, params: Dict = {}) => {
  mixpanel.track(eventName, params);
};

interface IdentifyParameter {
  emailAddress: string | undefined;
  id?: string | null;
  phoneNumber?: string;
  firstName?: string;
  lastName?: string;
}
export const identifyUser = ({
  emailAddress = '',
  id = '',
  phoneNumber = '',
  firstName = '',
  lastName = '',
}: IdentifyParameter) => {
  if (!analyticsEnabled()) return;

  // Snapchat
  if (window.snaptr && emailAddress) {
    window.snaptr('init', 'b46bf77a-84c8-420b-8e36-5fa5d97abceb', {
      user_email: emailAddress,
      user_phone_number: phoneNumber,
    });
  }

  // TikTok
  if (window.ttq && emailAddress) {
    window.ttq.identify({
      email: sha256(emailAddress.toLowerCase().trim()),
      external_id: id ? sha256(id) : undefined,
    });
  }

  // Sendinblue
  if (window.sendinblue && emailAddress) {
    window.sendinblue.identify(emailAddress, {
      FIRSTNAME: firstName,
    });
  }

  // Hotjar
  if (window.hj) {
    const utmParamsStr = localStorage.getItem('utm_params');
    let utmParams = undefined;
    try {
      utmParams = (utmParamsStr && JSON.parse(utmParamsStr)) || undefined;
    } catch {}

    const userId = id || null; // Replace your_user_id with your own if available.

    window.hj('identify', userId, utmParams);
  }

  if (id) {
    mixpanel.identify(id);
  }

  const registerProps: Dict = {};
  if (emailAddress) {
    registerProps.$email = emailAddress;
  }

  if (firstName && lastName) {
    registerProps.name = firstName + ' ' + lastName;
  }

  if (Object.keys(registerProps).length > 0) {
    mixpanel.people.set(registerProps);
  }
};

export const trackSignOutUser = (callback?: Callback) => {
  mixpanel.track('Sign Out', undefined, { send_immediately: true }, callback);
  mixpanel.reset();
};

export const trackRegistrationComplete = (
  emailAddress: string,
  referralId: string,
  firstName: string,
  lastName: string
) => {
  if (!analyticsEnabled()) return;

  // GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'register',
    });
  }

  if (window.fbq) {
    window.fbq('track', 'CompleteRegistration');
  }

  if (window.snaptr) {
    window.snaptr('track', 'SIGN_UP');
  }

  // TikTok
  if (window.ttq) {
    window.ttq.track('CompleteRegistration');
  }

  // Sendinblue
  if (window.sendinblue) {
    // Make sure the identify command has had a chance to run
    sleep(200).then(() => {
      window.sendinblue?.track(
        'register',
        {
          email: emailAddress,
          REFERRAL_CODE: referralId,
        },
        null
      );
    });
  }

  mixpanel.track('Register');
  mixpanel.register_once({
    'First Login Date': new Date().toISOString(),
    ReferralCode: referralId,
  });
};

export const setVariant = (variant: string | undefined | null) => {
  mixpanel.register({ variant });
};

export const setUserProperty = (key: string, value: string) => {
  const props: Record<string, string> = {};
  props[key] = value;
  mixpanel.register(props);
};

// Below, you can find all the Google Analytics Enhanced E-Commerce (GA EEC) tracking functions.
// For more info, check out this Notion page: https://www.notion.so/daymade/GA-EEC-deb07878d4f84db1874ee6c65b29ed5d

export const EecProduct = {
  LOTTERY: 'LOTTERY',
  POUND_RAFFLE: 'POUND_RAFFLE',
  UKRAINE_RELIEF_RAFFLE: 'UKRAINE_RELIEF_RAFFLE',
};

const eecProductInfo = (eecProduct: string, entryMethod?: string) => {
  switch (eecProduct) {
    case EecProduct.LOTTERY:
      switch (entryMethod) {
        case EntryMethod.PremiumSubscription:
          return {
            name: 'Premium Weekly Draw Subscription',
            id: 'premium-lottery-subscription',
            price: '3.00',
            url: `https://${APP_ENDPOINT}/enter?em=PremiumSubscription`,
            image: `${ASSET_PREFIX}/images/hero-new.jpg`,
          };

        case EntryMethod.LiteSubscription:
          return {
            name: 'Lite Weekly Draw Subscription',
            id: 'lite-lottery-subscription',
            price: '1.50',
            url: `https://${APP_ENDPOINT}/enter?em=LiteSubscription`,
            image: `${ASSET_PREFIX}/images/hero-new.jpg`,
          };

        case EntryMethod.OneOff:
          return {
            name: 'Weekly Draw One-Off Ticket',
            id: 'lottery-one-off',
            price: '4.00',
            url: `https://${APP_ENDPOINT}/enter?withOneOff=true`,
            image: `${ASSET_PREFIX}/images/hero-new.jpg`,
          };
        case EntryMethod.OneOffLite:
          return {
            name: 'Weekly Draw One-Off Lite Ticket',
            id: 'lottery-one-off',
            price: '2.00',
            url: `https://${APP_ENDPOINT}/enter?withOneOffLite=true`,
            image: `${ASSET_PREFIX}/images/hero-new.jpg`,
          };

        case EntryMethod.FreeSubscription:
          return {
            name: 'Free Weekly Draw Subscription',
            id: 'freemium-lottery-subscription',
            price: '0.00',
            url: `https://${APP_ENDPOINT}/enter?em=FreeSubscription`,
            image: `${ASSET_PREFIX}/images/hero-new.jpg`,
          };

        default:
          throw new Error(
            `Don't know what EEC product to return for Lottery product and ${entryMethod} entry method`
          );
      }

    case EecProduct.POUND_RAFFLE:
      return {
        name: 'Pound Raffle',
        id: 'raffle-one-off',
        price: '1.00',
        url: `https://${APP_ENDPOINT}/pound-raffle`,
        image: `${ASSET_PREFIX}/images/daily-pound-raffle/thumbnail.jpg`,
      };

    case EecProduct.UKRAINE_RELIEF_RAFFLE:
      return {
        name: 'Ukraine Relief Raffle',
        id: 'raffle-one-off',
        price: '3.00',
        url: `https://${APP_ENDPOINT}/ukraine-relief-raffle`,
        image: `${ASSET_PREFIX}/images/daily-pound-raffle/thumbnail.jpg`,
      };

    default:
      throw new Error(`Don't know what to do with eecProduct: ${eecProduct}`);
  }
};

/*
Arif (GA consultant) has instructed us to perform this function before every dataLayer push that has the `ecommerce` object in it.
The reason for this is given in his email:

  I have started with a normal lottery product and purchased it (I selected the £2 raffle checkbox to be able to add it to the order), after that I jumped to the raffle product and added the subscription one to the Cart. When it comes to the checkout / purchase GA4 pushed 2 products, 1 for subscription one and the other was £2 raffle (the one that I choose in my previous order).
  I think this is because of being in a SPA app and having the dataLayer filled on every action.
  The solution is simple though, can you call this row just before calling every dataLayer push that has "ecommerce" object in it?;

  dataLayer.push({ ecommerce: null });

  so having this row just before pushing the original ecommerce objects, will solve the problem!

*/
const clearDataLayerEcommerceProducts = () => {
  window.dataLayer.push({ ecommerce: null });
};

// Called when CTA linking to /enter or /raffle/enter is on-screen.
// This is so we can track which purchases came via which prize pages (GA Product List Performance report: https://analytics.google.com/analytics/web/?authuser=1#/report/conversions-ecommerce-product-list-performance/a140730926w201736875p195676788/).
export const trackEecImpression = async (
  productList: string,
  eecProduct: string
) => {
  if (!analyticsEnabled()) return true;

  let products: ReturnType<typeof eecProductInfo>[];
  if (eecProduct === EecProduct.POUND_RAFFLE) {
    products = [eecProductInfo(EecProduct.POUND_RAFFLE)];
  } else {
    products = [
      eecProductInfo(eecProduct, EntryMethod.PremiumSubscription),
      eecProductInfo(eecProduct, EntryMethod.LiteSubscription),
      eecProductInfo(eecProduct, EntryMethod.FreeSubscription),
      eecProductInfo(eecProduct, EntryMethod.OneOff),
    ];
  }

  // In case GTM script has not yet loaded.
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'eec.impression',
    ecommerce: {
      impressions: products.map((product, i) => ({
        ...product,
        list: productList,
        position: i,
      })),
    },
  });
};

// Once a user clicks one of the enter CTAs.
export const trackEecProductClick = async (
  productList: string,
  eecProduct: string
) => {
  if (!analyticsEnabled()) return true;

  mixpanel.track('Product Click', { productList, eecProduct });

  // In case GTM script has not yet loaded.
  window.dataLayer = window.dataLayer || [];

  clearDataLayerEcommerceProducts();

  let products;
  if (eecProduct === EecProduct.POUND_RAFFLE) {
    products = [eecProductInfo(EecProduct.POUND_RAFFLE)];
  } else {
    products = [
      eecProductInfo(eecProduct, EntryMethod.PremiumSubscription),
      eecProductInfo(eecProduct, EntryMethod.LiteSubscription),
      eecProductInfo(eecProduct, EntryMethod.FreeSubscription),
      eecProductInfo(eecProduct, EntryMethod.OneOff),
    ];
  }

  products.forEach((product) => {
    window.dataLayer.push({
      event: 'eec.productClick',
      ecommerce: {
        click: {
          actionField: {
            list: productList,
          },
          products: [
            {
              ...product,
            },
          ],
        },
      },
    });
  });
};

// When user clicks one of the frequency options (e.g. Premium/Lite/Freemium).
export const trackToggleSubscriptionFrequency = (
  eecProduct: string,
  entryMethod: string
) => {
  if (!analyticsEnabled()) return true;

  mixpanel.track('Subscription Click', { entryMethod, eecProduct });

  const productInfo = eecProductInfo(eecProduct, entryMethod);

  // In case GTM script has not yet loaded.
  window.dataLayer = window.dataLayer || [];

  clearDataLayerEcommerceProducts();

  // GTM EEC
  window.dataLayer.push({
    event: 'eec.productDetail',
    ecommerce: {
      detail: {
        products: [
          {
            ...productInfo,
          },
        ],
      },
    },
  });

  return true;
};

// Called when user clicks "continue" CTA after choosing their entry count.
// In this case, `variant` is the entry count (e.g. '3 Entries'). The GA property `quantity` refers to number of products purchased of *same price*, so we cannot use that to denote entry count (as our cost per entry varies).
export const trackEecAddToCart = async (
  eecProduct: string,
  entryMethod: string,
  variant: string,
  nonDiscountedPrice: number
) => {
  if (!analyticsEnabled()) return true;

  mixpanel.track('Draw Selected Click', { entryMethod, eecProduct });

  const productInfo = eecProductInfo(eecProduct, entryMethod);

  // In case GTM script has not yet loaded.
  window.dataLayer = window.dataLayer || [];

  clearDataLayerEcommerceProducts();

  window.dataLayer.push({
    event: 'eec.addToCart',
    ecommerce: {
      currencyCode: 'GBP',
      add: {
        products: [
          {
            ...productInfo,
            price: formattedPriceStringFromMicroCurrency(
              nonDiscountedPrice
            ).replace('£', ''),
            variant: variant,
            quantity: 1,
          },
        ],
      },
    },
  });

  // TikTok
  if (window.ttq) {
    window.ttq.track('AddToCart', {
      contents: [
        {
          content_id: productInfo.id,
          content_type: 'product',
          content_name: productInfo.name,
        },
      ],
      value: parseFloat(productInfo.price) * (parseInt(variant, 10) ?? 1),
      currency: 'GBP',
      // TikTok reckons we need an event_id, but we have nothing useful to send here
      // event_id:
    });
  }

  // Sendinblue
  if (window.sendinblue) {
    window.sendinblue.track(
      'cart_updated',
      null /* pass object here to update contact's properties */,
      {
        data: {
          products: [
            {
              name: productInfo.name,
              price: parseFloat(productInfo.price),
              url: productInfo.url,
              image: productInfo.image,
            },
          ],
        },
      }
    );
  }
};

// This event should be triggered alongside the above Add To Cart event. It signifies the start of the checkout funnel.
export const trackEecCheckoutStep1 = async (
  eecProduct: string,
  entryMethod: string,
  variant: string,
  nonDiscountedPrice: number
) => {
  if (!analyticsEnabled()) return true;

  const productInfo = eecProductInfo(eecProduct, entryMethod);

  mixpanel.track('Start Payment Flow', {
    entryMethod,
    eecProduct,
    variant,
    nonDiscountedPrice,
  });

  // In case GTM script has not yet loaded.
  window.dataLayer = window.dataLayer || [];

  clearDataLayerEcommerceProducts();

  window.dataLayer.push({
    event: 'eec.checkout',
    ecommerce: {
      checkout: {
        actionField: {
          step: 1,
        },
        products: [
          {
            ...productInfo,
            price: formattedPriceStringFromMicroCurrency(
              nonDiscountedPrice
            ).replace('£', ''),
            variant: variant,
            quantity: 1,
          },
        ],
      },
    },
  });
};

// This event should be triggered once user confirms their number selection.
export const trackEecCheckoutStep2 = async () => {
  if (!analyticsEnabled()) return true;

  mixpanel.track('Payment Flow Confirm Numbers');

  // In case GTM script has not yet loaded.
  window.dataLayer = window.dataLayer || [];

  clearDataLayerEcommerceProducts();

  window.dataLayer.push({
    event: 'eec.checkout',
    ecommerce: {
      checkout: {
        actionField: {
          step: 2,
        },
      },
    },
  });
};

// Triggered when user authenticates (registers or logs in). If user is already logged in, this event is fired automatically straight after Checkout Step 2 above.
export const trackEecCheckoutStep3 = async () => {
  if (!analyticsEnabled()) return true;

  mixpanel.track('Payment Flow User Logged in');

  // In case GTM script has not yet loaded.
  window.dataLayer = window.dataLayer || [];

  clearDataLayerEcommerceProducts();

  window.dataLayer.push({
    event: 'eec.checkout',
    ecommerce: {
      checkout: {
        actionField: {
          step: 3,
        },
      },
    },
  });

  // Pinterest Track AddToCart event
  // @ts-ignore
  if (window.pintrk) {
    // @ts-ignore
    window.pintrk('track', 'addtocart');
  }
};

// Once Continue To Payment CTA is pressed, this event fires. Product info is amended if an add-on raffle ticket is added.
export const trackEecCheckoutStep4 = async (
  eecProduct: string,
  entryMethod: string,
  variant: string,
  nonDiscountedPrice: number,
  includeAddOnRaffleTicket: boolean
) => {
  if (!analyticsEnabled()) return true;

  mixpanel.track('Payment Flow Continue to Payment', {
    eecProduct,
    entryMethod,
    variant,
    nonDiscountedPrice,
    includeAddOnRaffleTicket,
  });

  const amendProductInfo = includeAddOnRaffleTicket;

  let products;
  if (amendProductInfo) {
    const mainProductInfo = eecProductInfo(eecProduct, entryMethod);
    const addOnRaffleTicketInfo = eecProductInfo(EecProduct.POUND_RAFFLE);

    products = [
      {
        ...mainProductInfo,
        price: formattedPriceStringFromMicroCurrency(
          nonDiscountedPrice
        ).replace('£', ''),
        variant: variant,
        quantity: 1,
      },
      {
        ...addOnRaffleTicketInfo,
        variant: '1 entry',
        quantity: 1,
      },
    ];
  }

  // In case GTM script has not yet loaded.
  window.dataLayer = window.dataLayer || [];

  clearDataLayerEcommerceProducts();

  window.dataLayer.push({
    event: 'eec.checkout',
    ecommerce: {
      checkout: {
        actionField: {
          step: 4,
        },
        ...(amendProductInfo && {
          products: products,
        }),
      },
    },
  });
};

// Once one of the payment methods successfully returns (e.g. card or PayPal), we should fire the checkoutOption event with payment method used (along with a step of '4').
export const trackEecCheckoutOption = async (paymentMethod: string) => {
  if (!analyticsEnabled()) return true;

  mixpanel.track('Payment Flow Payment Complete', {
    paymentMethod,
  });

  // In case GTM script has not yet loaded.
  window.dataLayer = window.dataLayer || [];

  clearDataLayerEcommerceProducts();

  window.dataLayer.push({
    event: 'eec.checkoutOption',
    ecommerce: {
      checkout_option: {
        actionField: {
          step: 4, // Should this be a new step (5)?
          option: paymentMethod,
        },
      },
    },
  });
};

export const trackPurchase = async (
  purchaseValueMicro: string,
  orderId: string,
  order: TOrderDetails['order'],
  drawOrder: TOrderDetails['drawOrder'],
  drawSchedulePlans: TOrderDetails['drawSchedulePlans'],
  hashedEmailAddress: string,
  promoCodeUsed: string | null,
  userId: string | undefined,
  otherParams = {}
) => {
  // window.criteo_q = window.criteo_q || [];
  // const deviceType = /iPad/.test(navigator.userAgent) ? "t" : /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(navigator.userAgent) ? "m" : "d";
  // window.criteo_q.push(
  //   { event: "setAccount", account: 75430 }, // You should never update this line
  //   { event: "setEmail", email: hashedEmailAddress }, // Can be an empty string
  //   { event: "setSiteType", type: deviceType},
  //   { event: "trackTransaction", id: orderId, item: [ {id: "1", price: purchaseValueMicro / 10000, quantity: 1 } ]}
  // );

  if (!analyticsEnabled()) return;

  mixpanel.track('Payment Flow Purchase Complete', {
    purchaseValueMicro,
    orderId,
    order,
    drawSchedulePlans,
    drawOrder,
    hashedEmailAddress,
    promoCodeUsed,
    userId,
    ...otherParams,
  });

  const planCode = drawSchedulePlans
    ? drawSchedulePlans[0].drawTypePlan.code
    : undefined;
  const entryMethod =
    planCode == 'PREMIUM'
      ? EntryMethod.PremiumSubscription
      : planCode == 'LITE'
        ? EntryMethod.LiteSubscription
        : planCode == 'FREEMIUM'
          ? EntryMethod.FreeSubscription
          : undefined;

  const lotteryEntryCount = drawOrder ? drawOrder.entries.length : 0;

  // Check whether we have already tracked this purchase before.
  const lastOrderId = localStorage.getItem('last_order_id');
  if (lastOrderId === orderId.toString()) return;
  localStorage.setItem('last_order_id', orderId + '');

  const mainEecProduct = EecProduct.LOTTERY;
  const mainProductInfo = eecProductInfo(mainEecProduct, entryMethod);

  // These calculations are needed to work out the full un-discounted price of the product for GA EEC tracking.
  const mainProductQuantity = lotteryEntryCount;
  const mainProductPrice = drawOrder?.price;

  /** The very loose interface of a Product for the EecPurchase event, so that TypeScript is happy
   * but without changing any code
   */
  interface EecPurchaseProduct {
    variant: string;
    price?: string;
    quantity: number;
    name?: string;
    id?: string;
    url?: string;
    image?: string;
  }
  const products: EecPurchaseProduct[] = [
    {
      ...mainProductInfo,
      variant: `${mainProductQuantity} entry`, // quantity here because different quantities cost different amounts.
      price: formattedPriceStringFromPence(mainProductPrice || 0).replace(
        '£',
        ''
      ),
      quantity: 1,
    },
  ];

  // GA EEC final checkout flow event
  if (window.dataLayer) {
    clearDataLayerEcommerceProducts();

    window.dataLayer.push({
      event: 'eec.purchase',
      ecommerce: {
        purchase: {
          actionField: {
            id: `${orderId}`,
            revenue: `${+purchaseValueMicro / 10000}`,
            tax: 0,
            shipping: 0,
            ...(promoCodeUsed && {
              coupon: promoCodeUsed,
            }),
          },
          products: products,
        },
      },
    });
  }

  if (window.fbq) {
    window.fbq(
      'track',
      'Purchase',
      {
        currency: 'GBP',
        value: +purchaseValueMicro / 10000,
        external_id: userId,
      },
      {
        eventID: orderId,
      }
    );

    const paidSubscription =
      entryMethod &&
      [EntryMethod.PremiumSubscription, EntryMethod.LiteSubscription].includes(
        entryMethod
      );

    if (paidSubscription) {
      window.fbq(
        'trackCustom',
        'SubscriptionPurchase',
        {
          currency: 'GBP',
          value: +purchaseValueMicro / 10000,
          external_id: userId,
        },
        {
          eventID: orderId,
        }
      );
    }

    if (entryMethod === EntryMethod.PremiumSubscription) {
      window.fbq(
        'trackCustom',
        'PremiumPurchase',
        {
          currency: 'GBP',
          value: +purchaseValueMicro / 10000,
          external_id: userId,
        },
        {
          eventID: orderId,
        }
      );
    } else if (entryMethod === EntryMethod.LiteSubscription) {
      window.fbq(
        'trackCustom',
        'LitePurchase',
        {
          currency: 'GBP',
          value: +purchaseValueMicro / 10000,
          external_id: userId,
        },
        {
          eventID: orderId,
        }
      );
    }

    window.fbq(
      'trackCustom',
      'LotteryTicketPurchase',
      {
        currency: 'GBP',
        value: +purchaseValueMicro / 10000,
        external_id: userId,
      },
      {
        eventID: orderId,
      }
    );
  }

  if (window.snaptr) {
    window.snaptr('track', 'PURCHASE', {
      currency: 'GBP',
      price: +purchaseValueMicro / 10000,
      transaction_id: orderId,
      number_of_items: mainProductQuantity,
      user_hashed_email: hashedEmailAddress,
    });
  }

  if (window.pintrk) {
    window.pintrk('track', 'checkout', {
      order_id: orderId,
      value: +purchaseValueMicro / 10000,
      order_quantity: mainProductQuantity,
      currency: 'GBP',
    });
  }

  // TikTok
  if (window.ttq) {
    window.ttq.track('Purchase', {
      contents: [
        {
          content_id: mainProductInfo.id,
          content_type: 'product',
          content_name: mainProductInfo.name,
        },
      ],
      value: parseFloat(mainProductInfo.price) * (mainProductQuantity ?? 1),
      currency: 'GBP',
      event_id: sha256(orderId + ''),
    });
  }

  // Taboola
  if (window._tfa) {
    window._tfa.push({
      notify: 'event',
      name: 'subscription_purchase',
      id: 1367394,
      orderid: orderId,
      quantity: mainProductQuantity,
      revenue: +purchaseValueMicro / 10000,
    });
  }
  //Podscribe
  // @ts-ignore
  if (window.podscribe) {
    // @ts-ignore
    window.podscribe('purchase', {
      value: parseFloat(mainProductInfo.price) * (mainProductQuantity ?? 1),
      order_number: orderId,
      discount_code: promoCodeUsed,
      hashed_email: hashedEmailAddress,
    });
  }
  //Twitter
  // @ts-ignore
  if (window.twq) {
    // @ts-ignore
    // Insert Twitter Event ID
    window.twq('event', 'tw-oevac-of2w4', {
      value: +purchaseValueMicro / 10000, // use this to pass the value of the conversion (e.g. 5.00)
      currency: 'GBP', // use this to pass the currency of the conversion with an ISO 4217 code (e.g. ‘USD’)
      contents: [
        // use this to pass an array of products or content
        // add all items to the array
        // use this for the first item
        {
          content_type: 'product',
          content_id: mainProductInfo.id,
          content_name: mainProductInfo.name,
          content_price: mainProductInfo.price,
          num_items: mainProductQuantity,
          content_group_id: null,
        },
      ],
      conversion_id: orderId, // use this to pass a unique ID for the conversion event for deduplication (e.g. order id '1a2b3c')
    });
  }

  // Sendinblue
  // @ts-ignore
  if (window.sendinblue) {
    // @ts-ignore
    window.sendinblue.track(
      'purchase',
      {
        data: mainProductInfo.name,
      } /* pass object here to update contact's properties */,
      {
        data: {
          revenue:
            parseFloat(mainProductInfo.price) * (mainProductQuantity ?? 1),
          products: [
            {
              name: mainProductInfo.name,
              price:
                parseFloat(mainProductInfo.price) * (mainProductQuantity ?? 1),
            },
          ],
        },
      }
    );
  }
};

// When user starts free Weekly Draw subscription.
export const trackFreemiumPurchase = (
  subscriptionId: string,
  userId: string
) => {
  if (!analyticsEnabled()) return;

  mixpanel.track('Freemium Purchase', {
    subscriptionId,
    userId,
  });

  // In case GTM script has not yet loaded.
  window.dataLayer = window.dataLayer || [];

  // Awin data layer
  window.dataLayer.push({
    transactionTotal: 0,
    transactionCurrency: 'GBP',
    transactionID: `FS_${subscriptionId}`,
    transactionPromoCode: null,
    event: 'awin.dl.ready',
  });

  if (window.fbq) {
    window.fbq(
      'trackCustom',
      'FreemiumPurchase',
      {
        external_id: userId,
      },
      {
        eventID: `FS-${subscriptionId}`,
      }
    );
  }

  // Sendinblue
  if (window.sendinblue) {
    window.sendinblue.track('start_free_subscription');
  }
};

// Daily Challenger (Free Daily Draw)
export const trackFreeDrawPurchase = () => {
  if (!analyticsEnabled()) return;

  mixpanel.track('Free Draw Purchase');

  // GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'FreeDrawPurchase',
    });
  }

  if (window.fbq) {
    window.fbq('trackCustom', 'FreeDrawPurchase');
  }

  if (window.snaptr) {
    window.snaptr('track', 'FREE_DRAW_PURCHASE');
  }
};

// Non-GA tracking
export const trackInitiateCheckoutEvent = () => {
  if (!analyticsEnabled()) return;

  if (window.fbq) {
    window.fbq('track', 'InitiateCheckout');
  }

  if (window.snaptr) {
    window.snaptr('track', 'START_CHECKOUT');
  }

  // TikTok
  if (window.ttq) {
    window.ttq.track('InitiateCheckout');
  }
};

export const trackMailingListSignup = (emailAddress: string) => {
  mixpanel.track('Mailing list signup');

  // GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'mailing_list_signup',
    });
  }

  // Sendinblue
  if (window.sendinblue) {
    // Make sure the identify command has had a chance to run
    sleep(200).then(() => {
      window.sendinblue?.track(
        'mailing_list_signup',
        {
          email: emailAddress,
        },
        null
      );
    });
  }
};

// This method is called before making a purchase in order to retrieve UTM parameters from localStorage (if there are any).
// Appended to this is the fbc and fbp cookie values (if present) which allow our API to do server-side conversion mapping with
// Facebook's Conversions API.
export const fetchUtmParamsJoinedWithFbDataFromLocalStorage = () => {
  let purchaseUtmParams: Record<string, unknown> = {};
  const purchaseUtmParamsString = localStorage.getItem('utm_params');
  if (purchaseUtmParamsString) {
    const json = JSON.parse(purchaseUtmParamsString);
    if (json && json.utm_source !== null) {
      purchaseUtmParams = json;
    }
  }

  // Attach fbc and fbp values so we can track this purchase using FB's Conversion API server-side in case local tracking fails.
  const fbc = getCookie('_fbc');
  const fbp = getCookie('_fbp');
  if (
    (typeof fbc !== 'undefined' && fbc !== null) ||
    (typeof fbp !== 'undefined' && fbp !== null)
  ) {
    purchaseUtmParams.fbc = fbc;
    purchaseUtmParams.fbp = fbp;
  }

  // Attach the HeadAI order ID if it exists
  const ahaOrderId = window.ahaTracker?.payload?.orderId;
  if (ahaOrderId) {
    purchaseUtmParams.ahaOrderId = ahaOrderId;
  }

  if (Object.keys(purchaseUtmParams).length === 0) {
    return null;
  }

  return purchaseUtmParams;
};

type TSubscriptionResult = ClientInferResponseBody<
  typeof subscriptionContract.admin.getSubscriptionsForPlayer,
  200
>;

export const trackSubscriptionCancelled = (subscriptionInfo: {
  drawSchedulePlans: {
    drawTypePlan: Pick<
      TSubscriptionResult[0]['drawSchedulePlans'][0]['drawTypePlan'],
      'code'
    >;
  }[];
}) => {
  if (!analyticsEnabled()) return;

  mixpanel.track('Subscription cancelled');

  // Sendinblue
  if (window.sendinblue) {
    let subscriptionType =
      subscriptionInfo.drawSchedulePlans[0].drawTypePlan.code;
    if (subscriptionType === 'WEEKLY') subscriptionType = 'PREMIUM';
    if (subscriptionType === 'MONTHLY') subscriptionType = 'MONTHLY_PREMIUM';
    window.sendinblue.track(
      'subscription_cancelled',
      null /* pass object here to update contact's properties */,
      {
        data: { subscriptionType },
      }
    );
  }
};

export const trackSubscriptionDowngraded = (
  subscriptionInfo: {
    drawSchedulePlans: {
      drawTypePlan: Pick<
        TSubscriptionResult[0]['drawSchedulePlans'][0]['drawTypePlan'],
        'code'
      >;
    }[];
  },
  first_name: string,
  email_address: string
) => {
  if (!analyticsEnabled()) return;
  mixpanel.track('Subscription downgraded');

  // Sendinblue
  // @ts-ignore
  if (window.sendinblue) {
    let subscriptionType =
      subscriptionInfo.drawSchedulePlans[0].drawTypePlan.code;
    if (subscriptionType === 'WEEKLY') subscriptionType = 'PREMIUM';
    if (subscriptionType === 'MONTHLY') subscriptionType = 'MONTHLY_PREMIUM';
    // @ts-ignore
    window.sendinblue.track(
      'subscription_downgraded',
      { email_address, first_name },
      {
        data: { subscriptionType },
      }
    );
  }
};
